import { useEffect, useRef, useState } from 'react'
import type { AircraftSummary } from '../state/store'
import { emergencyLabel } from '../game/emergencies'
import type { Waypoint } from '../types/LevelData'
import type { ClampedCommandResult } from '../game/CommandService'
import { HeadingDial } from './HeadingDial'
import { CollapsibleSection } from './CollapsibleSection'
import { AltitudeRibbon } from './AltitudeRibbon'

/// Side panel that opens when an aircraft is selected. Shows different
/// controls depending on the aircraft's state:
///   - Awaiting takeoff: a single "Cleared for takeoff" button
///   - Anything else: heading + altitude inputs and "Cleared to land"
///
/// Inputs reset to the live aircraft state any time the selection
/// changes, so the user starts editing from the current target.

export interface CommandPanelHandlers {
  onSetHeading(
    headingDeg: number,
    direction?: 'shortest' | 'left' | 'right',
  ): void
  onSetAltitude(altitudeFt: number): ClampedCommandResult | null
  onSetSpeed(speedKts: number): ClampedCommandResult | null
  onClearToLand(): void
  onClearedForApproach(): void
  onTakeoff(): void
  onGoAround(): void
  onDirectTo(waypointName: string | null): void
  onHold(waypointName: string): void
  onCancelHold(): void
  onDeselect(): void
  onHandoff(): void
}

export function CommandPanel(props: {
  aircraft: AircraftSummary
  waypoints: Waypoint[]
  handlers: CommandPanelHandlers
  /// Live "Handoff" level — surfaces the Hand off to Center action.
  isLive?: boolean
}) {
  const { aircraft, waypoints, handlers, isLive } = props
  const [speed, setSpeed] = useState(String(Math.round(aircraft.targetSpeedKts)))

  useEffect(() => {
    setSpeed(String(Math.round(aircraft.targetSpeedKts)))
  }, [aircraft.id])

  const submitSpeed = () => {
    const n = parseInt(speed, 10)
    if (!Number.isFinite(n)) return
    const result = handlers.onSetSpeed(n)
    if (result) setSpeed(String(result.commanded))
  }

  return (
    // Two layouts share this DOM:
    //   • Phone (default): full-width bottom sheet up to 60% of
    //     viewport height, scroll inside the sheet. Keeps the
    //     radar visible above so the player can keep eyes on the
    //     aircraft they just selected.
    //   • Desktop (md and up): side panel on the right, like before.
    <aside className="pointer-events-auto absolute z-20 bg-slate-900/85 text-sm text-slate-100 shadow-xl ring-1 ring-white/10 backdrop-blur
      inset-x-0 bottom-0 max-h-[60vh] overflow-y-auto rounded-t-2xl p-4
      md:inset-x-auto md:bottom-auto md:right-4 md:top-16 md:w-64 md:max-h-[calc(100vh-5rem)] md:overflow-y-auto md:rounded-lg">
      <header className="flex items-baseline justify-between">
        <div>
          <div className="text-base font-semibold tracking-wide">{aircraft.flightNumber}</div>
          <div className="text-xs text-slate-400">{statusLabel(aircraft)}</div>
        </div>
        <button
          onClick={handlers.onDeselect}
          className="rounded px-2 py-1 text-xs text-slate-400 hover:bg-white/5 hover:text-white"
        >
          ✕
        </button>
      </header>

      {isLive && aircraft.canHandoff && (
        <button
          onClick={handlers.onHandoff}
          title="Clear this departing/transiting aircraft to the next sector. Arrivals can't be handed off — land them."
          className="mt-2 w-full rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
        >
          ✋ Hand off to Center
        </button>
      )}

      <div className="mt-2 rounded-md bg-slate-800/60 px-2 py-1 text-xs text-slate-300">
        <span className="text-slate-500">Goal:</span>{' '}
        <span className="font-medium text-slate-100">{aircraft.goalDescription}</span>
      </div>

      {aircraft.emergency && (
        <div className="mt-3 rounded-md bg-rose-900/60 px-2 py-1.5 text-xs font-semibold text-rose-100 ring-1 ring-rose-500/40">
          ⚠ EMERGENCY — {emergencyLabel(aircraft.emergency.reason)}
          <span className="ml-2 font-mono text-rose-200">
            {formatCountdown(aircraft.emergency.countdownSec)}
          </span>
        </div>
      )}
      {!aircraft.emergency && aircraft.collisionStatus !== 'normal' && (
        <CollisionBanner
          status={aircraft.collisionStatus}
          detail={aircraft.collisionDetail}
        />
      )}

      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-400">
        <dt>Heading</dt>
        <dd className="text-right text-slate-200">
          {Math.round(aircraft.headingDeg).toString().padStart(3, '0')}°
        </dd>
        <dt>Altitude</dt>
        <dd className="text-right text-slate-200">
          {Math.round(aircraft.altitudeFt).toLocaleString()} ft
        </dd>
        <dt>Speed</dt>
        <dd className="text-right text-slate-200">{Math.round(aircraft.speedKts)} kt</dd>
      </dl>

      <div className="mt-4 space-y-3">
        {aircraft.awaitingTakeoff ? (
          <button
            onClick={handlers.onTakeoff}
            className="w-full rounded-md bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-900 shadow hover:bg-amber-400"
          >
            Cleared for takeoff
            {aircraft.directToName ? (
              <span className="ml-1 font-normal text-slate-800">→ {aircraft.directToName}</span>
            ) : null}
          </button>
        ) : (
          <div key={aircraft.id} className="space-y-2">
            <CollapsibleSection label="Vectors" defaultOpen>
              {/* Stacked vertically — the dial is square and the
                  altitude ribbon is tall, so they don't share a
                  column gracefully. Each gets the full section
                  width at its natural shape. */}
              <div className="space-y-4">
                <HeadingDial
                  key={aircraft.id}
                  currentHeadingDeg={aircraft.headingDeg}
                  targetHeadingDeg={aircraft.targetHeadingDeg}
                  onSubmit={handlers.onSetHeading}
                />
                <AltitudeRibbon
                  currentAltitudeFt={aircraft.altitudeFt}
                  targetAltitudeFt={aircraft.targetAltitudeFt}
                  onSubmit={handlers.onSetAltitude}
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection label="Speed">
              <div className="space-y-2">
                <Field
                  label={`Set speed (${aircraft.minSpeedKts}–${aircraft.maxSpeedKts})`}
                  suffix="kt"
                  value={speed}
                  onChange={setSpeed}
                  onSubmit={submitSpeed}
                />
                {/* Hover button — helicopter-only. Pins target speed to
                    0 so the aircraft holds position. Helicopter
                    advancePosition no-ops at zero speed and the
                    clearance autopilot doesn't touch targetSpeedKts,
                    so a hovering helicopter cleared to land descends
                    in place — vertical landing in practice. */}
                {aircraft.type === 'helicopter' && (
                  <button
                    onClick={() => {
                      const result = handlers.onSetSpeed(0)
                      if (result) setSpeed(String(result.commanded))
                    }}
                    className="w-full rounded-md bg-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 shadow hover:bg-slate-600"
                  >
                    ⬛ Hover (speed 0)
                  </button>
                )}
              </div>
            </CollapsibleSection>

            <CollapsibleSection label="Direct-to / Hold">
              <div className="space-y-2">
                <DirectToField
                  waypoints={waypoints}
                  currentName={aircraft.directToName}
                  onChange={handlers.onDirectTo}
                />
                {/* Cancel direct-to: contextual; only visible while a
                    directTo is active and the aircraft isn't landing
                    (landing's directTo is the threshold — `Go around`
                    handles that case below). */}
                {aircraft.directToName && aircraft.status !== 'landing' && (
                  <button
                    onClick={() => handlers.onDirectTo(null)}
                    className="w-full rounded-md bg-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 shadow hover:bg-slate-600"
                  >
                    ✕ Cancel direct to {aircraft.directToName}
                  </button>
                )}
                <HoldField waypoints={waypoints} onChange={handlers.onHold} />
                {/* In-hold indicator + Cancel-hold (contextual). */}
                {aircraft.hold && (
                  <div className="space-y-2">
                    <div className="rounded-md bg-purple-900/60 px-2 py-1.5 text-xs font-semibold text-purple-100 ring-1 ring-purple-500/40">
                      ↻ Hold at {aircraft.hold.fixName}
                      <span className="ml-2 font-normal opacity-80">
                        {holdPhaseLabel(aircraft.hold.phase)}
                      </span>
                    </div>
                    <button
                      onClick={handlers.onCancelHold}
                      className="w-full rounded-md bg-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 shadow hover:bg-slate-600"
                    >
                      ✕ Cancel hold
                    </button>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            <CollapsibleSection label="Approach">
              <div className="space-y-2">
                {/* Approach-intercept indicator: replaces nothing else, just
                    a status pill so the player can see the autopilot is
                    driving heading. Vectoring (entering a heading) cancels. */}
                {aircraft.inApproachIntercept && aircraft.status !== 'landing' && (
                  <div className="rounded-md bg-sky-900/60 px-2 py-1.5 text-xs font-semibold text-sky-100 ring-1 ring-sky-500/40">
                    ↘ On localizer — autopilot tracking
                  </div>
                )}
                {/* Contextual Cleared-for-approach: visible only while the
                    aircraft is within 15 NM and ≤45° off the localizer of
                    the active runway, and not already on the intercept. */}
                {aircraft.canClearForApproach && !aircraft.inApproachIntercept && (
                  <button
                    onClick={handlers.onClearedForApproach}
                    title="Engage the localizer-intercept autopilot. Aircraft will turn onto the runway extended centerline; altitude stays under your control until you issue Cleared to land."
                    className="w-full rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-sky-500"
                  >
                    ↘ Cleared for approach
                  </button>
                )}
                {/* Placeholder when neither indicator nor button renders, so
                    the section isn't mysteriously empty. */}
                {!aircraft.inApproachIntercept &&
                  !(aircraft.canClearForApproach && !aircraft.inApproachIntercept) && (
                    <p className="text-xs italic text-slate-500">
                      Not yet within approach range.
                    </p>
                  )}
              </div>
            </CollapsibleSection>

            {/* Cleared-to-land / Go-around — pinned below the sections. */}
            {aircraft.status === 'landing' ? (
              <button
                onClick={handlers.onGoAround}
                title="Abort the approach — climb to ~4,000 ft AGL and revert to en route. You can re-vector for another attempt."
                className="w-full rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-rose-500"
              >
                ⤴ Go around
              </button>
            ) : (
              <button
                disabled={!aircraft.canClearToLand}
                onClick={handlers.onClearToLand}
                title={
                  aircraft.clearToLandRejection
                    ? rejectionTitle(aircraft.clearToLandRejection)
                    : undefined
                }
                className="w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
              >
                {aircraft.canClearToLand
                  ? 'Cleared to land'
                  : `Cleared to land — ${rejectionShortLabel(aircraft.clearToLandRejection)}`}
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  )
}

function HoldField(props: {
  waypoints: Waypoint[]
  onChange: (waypointName: string) => void
}) {
  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wide text-slate-400">
        Hold at
      </label>
      <select
        value=""
        onChange={(e) => {
          if (e.target.value !== '') props.onChange(e.target.value)
        }}
        className="w-full rounded-md bg-slate-800 px-2 py-1.5 font-mono text-sm text-slate-100 outline-none ring-1 ring-white/10 focus:ring-purple-500"
      >
        <option value="">— pick a fix —</option>
        {props.waypoints.map((wp) => (
          <option key={wp.name} value={wp.name}>
            {wp.name} ({wp.type})
          </option>
        ))}
      </select>
    </div>
  )
}

function holdPhaseLabel(phase: NonNullable<AircraftSummary['hold']>['phase']): string {
  switch (phase) {
    case 'flyingToWaypoint':
      return 'inbound'
    case 'firstTurn':
      return 'turn 1'
    case 'outboundLeg':
      return 'outbound'
    case 'secondTurn':
      return 'turn 2'
  }
}

function DirectToField(props: {
  waypoints: Waypoint[]
  currentName: string | null
  onChange: (waypointName: string | null) => void
}) {
  const value = props.currentName ?? ''
  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wide text-slate-400">
        Direct to
      </label>
      <select
        value={value}
        onChange={(e) => props.onChange(e.target.value === '' ? null : e.target.value)}
        className="w-full rounded-md bg-slate-800 px-2 py-1.5 font-mono text-sm text-slate-100 outline-none ring-1 ring-white/10 focus:ring-emerald-500"
      >
        <option value="">— none —</option>
        {props.waypoints.map((wp) => (
          <option key={wp.name} value={wp.name}>
            {wp.name} ({wp.type})
          </option>
        ))}
      </select>
    </div>
  )
}

function CollisionBanner(props: {
  status: AircraftSummary['collisionStatus']
  detail: string | null
}) {
  if (props.status === 'normal') return null
  // One palette per state — kept in sync with the AircraftLayer tint
  // priority so the panel feels like the same alert.
  let glyph = '⚠'
  let title = ''
  let className = ''
  switch (props.status) {
    case 'conflict':
      glyph = '▲'
      title = 'TRAFFIC'
      className =
        'bg-yellow-900/60 text-yellow-100 ring-yellow-500/40 ring-1'
      break
    case 'terrainWarning':
      glyph = '⛰'
      title = 'TERRAIN'
      className =
        'bg-orange-900/60 text-orange-100 ring-orange-500/40 ring-1'
      break
    case 'crashed':
      glyph = '✕'
      title = 'CRASHED'
      className = 'bg-rose-950/80 text-rose-200 ring-rose-700/60 ring-1'
      break
  }
  return (
    <div className={'mt-3 rounded-md px-2 py-1.5 text-xs font-semibold ' + className}>
      {glyph} {title}
      {props.detail ? <span className="ml-2 font-normal opacity-90">— {props.detail}</span> : null}
    </div>
  )
}

function formatCountdown(sec: number): string {
  const total = Math.max(0, Math.floor(sec))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function rejectionShortLabel(r: AircraftSummary['clearToLandRejection']): string {
  switch (r) {
    case 'not_aligned':
      return 'turn to runway'
    case 'off_centerline':
      return 'off centerline'
    case 'too_far':
      return 'too far'
    case 'descent_too_steep':
      return 'too high'
    case 'speed_zero':
      return 'no speed'
    case 'invalid_runway':
    case null:
    default:
      return 'unavailable'
  }
}

function rejectionTitle(r: AircraftSummary['clearToLandRejection']): string {
  switch (r) {
    case 'not_aligned':
      return 'Aircraft heading is more than 45° off the runway approach.'
    case 'off_centerline':
      return 'Aircraft is laterally off the runway extended centerline.'
    case 'too_far':
      return 'Aircraft is beyond the airport landing approach distance.'
    case 'descent_too_steep':
      return 'Required descent rate exceeds 3000 ft/min — descend further before clearing.'
    case 'speed_zero':
      return 'Aircraft has no airspeed.'
    case 'invalid_runway':
      return 'No suitable runway found.'
    default:
      return 'Cleared-to-land currently unavailable.'
  }
}

function statusLabel(a: AircraftSummary): string {
  if (a.hold) return `Holding ${a.hold.fixName}`
  switch (a.status) {
    case 'landing':
      return 'Cleared to land'
    case 'awaitingTakeoff':
      return a.departureRunway ? `Holding RWY ${a.departureRunway.runwayId}` : 'Awaiting takeoff'
    case 'takingOff':
      return 'Taking off'
    case 'completed':
      return 'Off radar'
    case 'landed':
      return 'Landed'
    case 'enRoute':
    default:
      return a.directToName ? `→ ${a.directToName}` : 'En route'
  }
}

function Field(props: {
  label: string
  suffix: string
  value: string
  onChange: (v: string) => void
  onSubmit: () => void
}) {
  // Track the last value we actually submitted so a blur that
  // immediately follows Enter (or tabbing through a field without
  // editing it) doesn't re-issue the same command — and re-fire its
  // readback. Seeded with the field's current value so the first
  // no-op tab-through is quiet too.
  //
  // Do NOT sync this ref to `props.value` via an effect: `props.value`
  // is the controlled input text and changes on every keystroke, so
  // the ref would always already equal it by the time Enter/blur
  // fires and EVERY submit would be swallowed — that bug killed the
  // whole command panel (heading/altitude/speed all dead) until this
  // fix.
  const lastSubmittedRef = useRef(props.value)
  const submitIfChanged = () => {
    if (props.value === lastSubmittedRef.current) return
    lastSubmittedRef.current = props.value
    props.onSubmit()
  }
  return (
    <div>
      <label className="mb-1 flex items-center justify-between text-xs uppercase tracking-wide text-slate-400">
        <span>{props.label}</span>
        <span className="text-[10px] font-normal normal-case text-slate-500">
          press <kbd className="rounded bg-slate-800 px-1 font-mono ring-1 ring-white/10">↵</kbd>{' '}
          or tab
        </span>
      </label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            inputMode="numeric"
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitIfChanged()
            }}
            // Tab-away (blur) also commits, so a player can keystroke
            // through heading → altitude → button without ever
            // touching the mouse. Dedup'd against the last-submitted
            // value so a blur immediately after Enter doesn't repeat
            // the readback.
            onBlur={submitIfChanged}
            className="w-full rounded-md bg-slate-800 px-2 py-1.5 pr-8 font-mono text-sm text-slate-100 outline-none ring-1 ring-white/10 focus:ring-emerald-500"
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">
            {props.suffix}
          </span>
        </div>
        {/* Wide tap target — player feedback (dev board, Dandop,
            2026-06-11): the old px-3/text-xs button was too small to
            adjust-then-confirm quickly. */}
        <button
          onClick={submitIfChanged}
          className="min-w-[76px] rounded-md bg-slate-700 px-5 text-sm font-semibold text-slate-100 hover:bg-slate-600"
        >
          Set
        </button>
      </div>
    </div>
  )
}
